import { handleTokenExpiration } from "./auth-utils";

// Store the original fetch function before we replace it
let originalFetch: typeof fetch = window.fetch;

// Track if we're already handling a logout to prevent multiple simultaneous logouts
let isLoggingOut = false;

/**
 * Check if the request URL should be excluded from 401 handling
 * (e.g., logout endpoint, login endpoint)
 */
function shouldExcludeFrom401Handling(url: string): boolean {
  // Don't handle 401s for auth endpoints that already manage their own 401 behaviour:
  // - /api/auth/user  → fetchUser() returns null for unauthenticated visitors (public pages)
  // - /api/auth/login → login errors are shown as form feedback, not a redirect
  // - /api/auth/logout → obvious
  return (
    url.includes("/api/auth/logout") ||
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/user")
  );
}

/**
 * Get URL string from RequestInfo | URL
 */
function getUrlString(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  if (input instanceof Request) {
    return input.url;
  }
  // Fallback for any other type
  try {
    return String(input);
  } catch {
    return "";
  }
}

/**
 * Intercepted fetch function that automatically handles 401 responses
 * by logging out the user
 */
export const interceptedFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  // Get the URL string for checking
  const urlString = getUrlString(input);

  // Use the original fetch to avoid infinite recursion
  const response = await originalFetch(input, init);

  // Intercept 401 responses and automatically log out
  // Skip handling for logout/login endpoints and if already logging out
  if (
    response.status === 401 &&
    !isLoggingOut &&
    !shouldExcludeFrom401Handling(urlString)
  ) {
    // Prevent multiple simultaneous logout attempts
    isLoggingOut = true;
    
    // Handle token expiration (shows toast and logs out)
    await handleTokenExpiration();
    
    // Reset flag after a delay to allow logout to complete
    setTimeout(() => {
      isLoggingOut = false;
    }, 2000);
  }

  return response;
};

/**
 * Initialize the HTTP interceptor by replacing the global fetch
 * This should be called once when the app starts
 */
export function setupHttpInterceptor() {
  // Store the original fetch before replacing it
  originalFetch = window.fetch;

  // Replace global fetch with our interceptor
  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    return interceptedFetch(input, init);
  };

  // Return a function to restore original fetch if needed
  return () => {
    if (originalFetch) {
      window.fetch = originalFetch;
    }
  };
}
